import { Action, createReducer, on } from '@ngrx/store';

import * as AuthActions from './auth.actions';
import * as AuthFeatureState from './auth.state';

const authReducer = createReducer<AuthFeatureState.AuthState>(
  AuthFeatureState.initialAuthState,

  on(AuthActions.initAuthState, (state) => ({
    ...state
  })),

  on(AuthActions.setSessionInitializing, (state, { initializing }) => ({
    ...state,
    isSessionInitializing: initializing
  })),

  on(AuthActions.saveTokenToState, (state, action) => ({
    ...state,
    token: action.token,
    isSessionLoaded: true,
    errorMessage: null
  })),

  // retrySessionFailed is listed explicitly: it used to share getSessionFailed's
  // action type string, so this case matched both. Keep matching both now that
  // the types differ.
  on(
    AuthActions.getSessionFailed,
    AuthActions.retrySessionFailed,
    (state, { errorMessage }) => ({
      ...state,
      token: null,
      isSessionLoaded: false,
      // STAFF-3 (H1). A dead session takes the staff sign-in with it, or the
      // header would go on showing a signed-in counselor with no session.
      isAuthenticated: false,
      role: null,
      errorMessage: errorMessage
    })
  ),

  on(AuthActions.adminLogInSuccess, (state, { user }) => ({
    ...state,
    token: user.token,
    isSessionLoaded: true,
    errorMessage: null,
    returnSessionResult: null,
    isAuthenticated: true,
    userId: user.userId,
    username: user.userName,
    userEmail: user.userEmail,
    firstName: user.profile?.firstName,
    lastName: user.profile?.lastName,
    role: user.userRole
  })),

  on(
    AuthActions.restoreAuthorizationState,
    (state, { role, isAuthenticated }) => {
      /*
      Session restore races an explicit login. On a cold page load the app
      calls getSession() with whatever token sessionStorage holds — in a fresh
      window that is nothing, so the backend mints an ANONYMOUS session. If the
      user signs in while that request is still in flight, the anonymous
      response lands AFTER adminLogInSuccess and used to overwrite role +
      isAuthenticated with null/false, silently un-authenticating someone who
      had just logged in. The role guards then bounced them to /error/401.

      Restore may only ever UPGRADE authorization. Dropping it belongs to the
      explicit sign-out path (adminLogOut), never to a late anonymous session.
      */
      if (!isAuthenticated && state.isAuthenticated) {
        return state;
      }

      return {
        ...state,
        role,
        isAuthenticated
      };
    }
  ),

  on(AuthActions.setAuthProfile, (state, { firstName, lastName }) => ({
    ...state,
    firstName,
    lastName
  })),

  on(AuthActions.adminLogInFailure, (state) => ({
    ...state,
    isAuthenticated: false,
    username: null,
    role: null,
    errorMessage: 'Log In Failed'
  })),

  on(AuthActions.adminLogOut, (state) => ({
    ...state,
    ...AuthFeatureState.initialAuthState
  })),

  on(
    AuthActions.setLoginWallRedirectUrl,
    (state, { loginWallRedirectUrl }) => ({ ...state, loginWallRedirectUrl })
  ),

  on(
    AuthActions.returnSessionProcessedSuccess,
    (state, { token, returnSessionResult }) => {
      return {
        ...AuthFeatureState.initialAuthState,
        shareToken: token,
        isSessionLoaded: true,
        returnSessionResult
      };
    }
  ),

  on(AuthActions.clearShareToken, (state) => ({
    ...state,
    shareToken: null
  })),

  on(
    AuthActions.returnSessionProcessedFailure,
    (state, { returnSessionResult }) => ({
      ...AuthFeatureState.initialAuthState,
      returnSessionResult
    })
  ),

  on(AuthActions.submitPasscode, (state, { passcode }) => ({
    ...state,
    isOverPasscode: passcode === '123456' // Example passcode check
  })),

  on(AuthActions.submitPasscodeSuccess, (state) => ({
    ...state,
    isOverPasscode: true
  })),

  on(AuthActions.submitPasscodeFailure, (state) => ({
    ...state,
    isOverPasscode: false,
    errorMessage: 'Passcode submission failed'
  })),

  on(AuthActions.deleteTokenFromStorage, (state) => ({
    ...state,
    token: null
  }))
);

export function reducer(
  state: AuthFeatureState.AuthState | undefined,
  action: Action
) {
  return authReducer(state, action);
}
